import { axios, getAuthDeviceId } from '@/utils/axios';
import { rememberPblUploadProof, withPblUploadProofs } from '@/utils/pblUploadProof';
import { getPblAssistanceToken } from '@/utils/pblAssistance';
import type {
  ApiResponse,
  Paginated,
  PblAssignmentRespondPayload,
  PblAppointmentProposal,
  PblAppointmentProposalCreatePayload,
  PblAppointmentProposalSelectPayload,
  PblAppointmentProposalSelectResponse,
  PblAppointmentProposalUpdatePayload,
  PblAuthTokenResponse,
  PblBlockchainRecord,
  PblBlockchainRecordPayload,
  PblBusinessDayAvailability,
  PblCalendarEvent,
  PblCase,
  PblCaseCreatePayload,
  PblCaseReviewPayload,
  PblCaseTransitionPayload,
  PblCancelCasePayload,
  PblCitizenContext,
  PblCitizenCaseCreatePayload,
  PblCitizenAuthorization,
  PblCitizenAuthorizationPayload,
  PblCitizenProfile,
  PblCitizenOtpIssuePayload,
  PblCitizenOtpIssueResult,
  PblCitizenOtpVerifyPayload,
  PblDashboard,
  PblDirectoryEntry,
  PblDocument,
  PblDocumentAccess,
  PblDocumentAccessPayload,
  PblDocumentCreatePayload,
  PblDocumentSignatureRequestPayload,
  PblEvaluation,
  PblEvaluationPayload,
  PblLawyerApplicationPayload,
  PblLawyerCvSummary,
  PblLawyerCvRevision,
  PblLawyerCvRevisionWorkspace,
  PblCvAuthorisation,
  PblStorageFile,
  PblLawyerExitRequest,
  PblLawyerExitRequestPayload,
  PblLawyerProfile,
  PblLawyerProfileTextTranslation,
  PblLawyerProfileTextTranslationPayload,
  PblCaseAssignment,
  PblManualInvitePayload,
  PblMeeting,
  PblMeetingCreatePayload,
  PblMeetingUpdatePayload,
  PblMatchingResult,
  PblMatchingRunResponse,
  PblMatchingStatusResponse,
  PblNotification,
  PblNotificationType,
  PblOcContext,
  PblOcAssistanceGrant,
  PblOcAssistanceSessionStart,
  PblOcIntakeMeetingCreatePayload,
  PblOcIntakeMeetingCreateResponse,
  PblIntakeSummaryResponse,
  PblPasswordlessExchangePayload,
  PblPasswordlessExchangeResponse,
  PblCitizenPasswordlessIssuePayload,
  PblCitizenPasswordlessIssueResponse,
  PblCitizenSmsLoginIssuePayload,
  PblCitizenSmsLoginIssueResponse,
  PblCitizenSmsLoginVerifyPayload,
  PblCitizenCaseDraftPayload,
  PblPasswordlessSetPasswordPayload,
  PblReassignLawyerPayload,
  PblServiceArea,
  PblSignatureRequest,
  PblSignatureStartResponse,
  PblSignatureUpload,
  PblUploadedFile,
  PblVettingReviewPayload,
  PblVettingStatus,
} from '@/types/pbl';

export const getMyPblLawyerApplication = () =>
  axios.get<ApiResponse<PblLawyerProfile | null>>('/api/pbl/lawyer/application');

export const getMyPblLawyerCvRevision = () =>
  axios.get<ApiResponse<PblLawyerCvRevisionWorkspace>>('/api/pbl/lawyer/cv-revision');

export const replaceMyPblFirmAuthorisation = (file: File) => {
  const data = new FormData();
  data.append('firm_authorisation_file', file);
  return axios.post<ApiResponse<PblStorageFile>>('/api/pbl/lawyer/firm-authorisation', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const saveMyPblLawyerCvDraft = (data: Partial<PblLawyerProfile>) =>
  axios.put<ApiResponse<PblLawyerCvRevision>>('/api/pbl/lawyer/cv-revision/draft', withPblUploadProofs(data));

export const getPblCvAuthorisation = (profileId: number, revisionId?: number) =>
  axios.get<Blob>(`/api/pbl/lawyer-profiles/${profileId}/cv-authorisation`, {
    params: { revision_id: revisionId },
    responseType: 'blob',
  });

export const submitMyPblLawyerCvRevision = () =>
  axios.post<ApiResponse<PblLawyerCvRevision>>('/api/pbl/lawyer/cv-revision/submit');

export const getPblLawyerCvReviewQueue = (params?: { page?: number; per_page?: number }) =>
  axios.get<ApiResponse<Paginated<PblLawyerCvRevision>>>('/api/pbl/vetting/cv-revisions', { params });

export const getPblLawyerCvRevisionForReview = (revisionId: number) =>
  axios.get<ApiResponse<{
    revision: PblLawyerCvRevision;
    published_payload: Partial<PblLawyerProfile>;
    published_authorisation?: PblCvAuthorisation;
  }>>(`/api/pbl/vetting/cv-revisions/${revisionId}`);

export const reviewPblLawyerCvRevision = (
  revisionId: number,
  data: { decision: 'approve' | 'return' | 'reject'; reason?: string }
) => axios.post<ApiResponse<PblLawyerCvRevision>>(`/api/pbl/vetting/cv-revisions/${revisionId}/review`, data);

export const submitPblLawyerApplication = (data: PblLawyerApplicationPayload | FormData) =>
  axios.post<ApiResponse<PblLawyerProfile>>('/api/pbl/lawyer/application', withPblUploadProofs(data));

export const issuePblLawyerApplicationEmailVerification = (data: { email: string }) =>
  axios.post<ApiResponse<{ one_time_token: string; email_exists: boolean }>>(
    '/api/pbl/lawyer/application/email/issue',
    data
  );

export const verifyPblLawyerApplicationEmailVerification = (data: {
  email: string;
  email_verification_token: string;
  email_verification_code: string;
}) =>
  axios.post<ApiResponse<{ verified: boolean; email_exists: boolean }>>(
    '/api/pbl/lawyer/application/email/verify',
    data
  );

export const issuePblLawyerPhoneOtp = (data: PblCitizenOtpIssuePayload) =>
  axios.post<ApiResponse<PblCitizenOtpIssueResult>>('/api/pbl/lawyer/application/phone/issue', data);

export const verifyPblLawyerPhoneOtp = (data: PblCitizenOtpVerifyPayload) =>
  axios.post<ApiResponse<{ verified: boolean; channel: 'phone'; phone_verification_token: string }>>(
    '/api/pbl/lawyer/application/phone/verify', data
  );

export const translatePblLawyerProfileText = (data: PblLawyerProfileTextTranslationPayload) =>
  axios.post<ApiResponse<PblLawyerProfileTextTranslation>>('/api/pbl/lawyer/application/translate-profile-text', data);

export const getProBonoDirectoryLawyers = (params?: {
  office_id?: number;
  service_area?: PblServiceArea | string;
  keyword?: string;
  case_id?: number;
  per_page?: number;
  page?: number;
}) => axios.get<ApiResponse<Paginated<PblDirectoryEntry>>>('/api/pbl/directory/lawyers', { params });

export const setPblDirectoryFavorite = (officeId: number, directoryEntryId: number, favorite: boolean) =>
  axios.request<ApiResponse<{ is_preferred: boolean; is_personal_favorite: boolean }>>({
    method: favorite ? 'put' : 'delete',
    url: `/api/pbl/directory/lawyers/${directoryEntryId}/favorite`,
    params: { office_id: officeId },
  });

export const preferPblLawyer = (caseId: number, directoryEntryId: number) =>
  axios.put<ApiResponse<{ is_preferred: boolean; is_personal_favorite: boolean }>>(
    `/api/pbl/cases/${caseId}/preferred-lawyers/${directoryEntryId}`
  );

export const unpreferPblLawyer = (caseId: number, directoryEntryId: number) =>
  axios.delete<ApiResponse<{ is_preferred: boolean; is_personal_favorite: boolean }>>(
    `/api/pbl/cases/${caseId}/preferred-lawyers/${directoryEntryId}`
  );

export const getPblNotifications = (params?: {
  channel?: 'in_app' | 'email' | 'sms';
  type?: PblNotificationType | string;
  status?: 'queued' | 'sent' | 'failed' | 'skipped' | 'read' | 'archived';
  archived?: boolean;
  per_page?: number;
  page?: number;
}) => axios.get<ApiResponse<Paginated<PblNotification>>>('/api/pbl/notifications', { params });

export const markPblNotificationRead = (notificationId: number) =>
  axios.post<ApiResponse<PblNotification>>(`/api/pbl/notifications/${notificationId}/read`);

export const archivePblNotification = (notificationId: number) =>
  axios.post<ApiResponse<PblNotification>>(`/api/pbl/notifications/${notificationId}/archive`);
export const unarchivePblNotification = (notificationId: number) =>
  axios.post<ApiResponse<PblNotification>>(`/api/pbl/notifications/${notificationId}/unarchive`);

export const getPblDashboard = () => axios.get<ApiResponse<PblDashboard>>('/api/pbl/dashboard');

export const getPblCalendarEvents = (params: { from?: string; to?: string }) =>
  axios.get<ApiResponse<PblCalendarEvent[]>>('/api/pbl/events/calendar', { params });

export const getPblNonBusinessDates = (params: { from: string; to: string }) =>
  axios.get<ApiResponse<PblBusinessDayAvailability>>('/api/pbl/business-days/non-working', { params });

export const getPblVettingApplications = (params?: { status?: PblVettingStatus; per_page?: number; page?: number }) =>
  axios.get<ApiResponse<Paginated<PblLawyerProfile>>>('/api/pbl/vetting/applications', { params });

export const getPblVettingApplication = (profileId: number) =>
  axios.get<ApiResponse<PblLawyerProfile>>(`/api/pbl/vetting/applications/${profileId}`);

export const reviewPblLawyerApplication = (profileId: number, data: PblVettingReviewPayload) =>
  axios.post<ApiResponse<PblLawyerProfile>>(`/api/pbl/vetting/applications/${profileId}/review`, data);

export const exchangePblPasswordlessToken = (data: PblPasswordlessExchangePayload) =>
  axios.post<ApiResponse<PblPasswordlessExchangeResponse>>('/api/pbl/passwordless/exchange', data);

export const setPblCitizenFirstPassword = (data: PblPasswordlessSetPasswordPayload) =>
  axios.post<ApiResponse<PblAuthTokenResponse>>('/api/pbl/passwordless/set-password', data);

export const issuePblCitizenPasswordlessLink = (data: PblCitizenPasswordlessIssuePayload) =>
  axios.post<ApiResponse<PblCitizenPasswordlessIssueResponse>>('/api/pbl/citizen/passwordless/issue', data);

export const issuePblCitizenSmsLoginOtp = (data: PblCitizenSmsLoginIssuePayload) =>
  axios.post<ApiResponse<PblCitizenSmsLoginIssueResponse>>('/api/pbl/citizen/sms-login/otp/issue', data);

export const verifyPblCitizenSmsLoginOtp = (data: PblCitizenSmsLoginVerifyPayload) =>
  axios.post<ApiResponse<PblAuthTokenResponse>>('/api/pbl/citizen/sms-login/otp/verify', data);

export const getPblCases = (params?: { status?: string; per_page?: number; page?: number }) =>
  axios.get<ApiResponse<Paginated<PblCase>>>('/api/pbl/cases', { params });

export const getPblCase = (caseId: number, signal?: AbortSignal) => axios.get<ApiResponse<PblCase>>(`/api/pbl/cases/${caseId}`, { signal });

export const getPblCaseLawyerCv = (caseId: number) =>
  axios.get<ApiResponse<PblLawyerCvSummary>>(`/api/pbl/cases/${caseId}/lawyer-cv`);

export const getPblCaseAuthorizationFormDownload = (caseId: number) =>
  axios.get<Blob>(`/api/pbl/cases/${caseId}/downloads/authorization-form`, {
    responseType: 'blob',
  });

export const getPblCasePersonalDetailsCaseDownload = (caseId: number) =>
  axios.get<Blob>(`/api/pbl/cases/${caseId}/downloads/personal-details-case`, {
    responseType: 'blob',
  });

export const createPblCitizenAuthorization = (data: PblCitizenAuthorizationPayload) =>
  axios.post<ApiResponse<PblCitizenAuthorization>>('/api/pbl/oc/citizens/authorization', withPblUploadProofs(data));

export const getPblOcCitizens = (params?: { keyword?: string; per_page?: number; page?: number }) =>
  axios.get<ApiResponse<Paginated<PblCitizenProfile>>>('/api/pbl/oc/citizens', { params });

export const getPblOcCitizen = (profileId: number) =>
  axios.get<ApiResponse<PblCitizenProfile>>(`/api/pbl/oc/citizens/${profileId}`);

export const uploadPblSignatureFile = (file: File) => {
  const formData = new FormData();
  formData.append('files[]', file);

  return axios.post<ApiResponse<PblSignatureUpload[]>>('/api/file-manager/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const uploadPblDocumentFile = uploadPblSignatureFile;

export const uploadPblFile = (data: FormData) =>
  axios.post<ApiResponse<PblUploadedFile>>(
    getPblAssistanceToken() ? '/api/pbl/assistance/uploads' : '/api/pbl/uploads', data, {
    headers: { 'Content-Type': 'multipart/form-data', 'X-Auth-Device': getAuthDeviceId() || '' },
    skipAuthRefresh: !getPblAssistanceToken(),
  }).then((response) => {
    rememberPblUploadProof(response.data.data);
    return response;
  });

export const issuePblCitizenOtp = (data: PblCitizenOtpIssuePayload) =>
  axios.post<ApiResponse<PblCitizenOtpIssueResult>>('/api/pbl/oc/citizens/otp/issue', data);

export const verifyPblCitizenOtp = (data: PblCitizenOtpVerifyPayload) =>
  axios.post<ApiResponse<{ verified: boolean; channel: 'email' | 'phone' }>>('/api/pbl/oc/citizens/otp/verify', data);

export const getPblOcContext = () => axios.get<ApiResponse<PblOcContext>>('/api/pbl/oc/context');

export const getPblOcAssistanceGrants = (profileId: number) =>
  axios.get<ApiResponse<PblOcAssistanceGrant[]>>(`/api/pbl/oc/citizens/${profileId}/assistance-grants`);

export const startPblOcAssistanceSession = (grantId: number) =>
  axios.post<ApiResponse<PblOcAssistanceSessionStart>>('/api/pbl/oc/assistance/sessions', { grant_id: grantId });

export const endPblOcAssistanceSession = (sessionId: number) =>
  axios.post<ApiResponse<unknown>>(`/api/pbl/oc/assistance/sessions/${sessionId}/end`);

export const getPblCitizenContext = () => axios.get<ApiResponse<PblCitizenContext>>('/api/pbl/citizen/context');

export const getCitizenPblCaseDraft = () =>
  axios.get<ApiResponse<PblCase | null>>('/api/pbl/citizen/cases/draft');

export const saveCitizenPblCaseDraft = (data: PblCitizenCaseDraftPayload) =>
  axios.post<ApiResponse<PblCase>>('/api/pbl/citizen/cases/draft', withPblUploadProofs(data));

export const createCitizenPblCase = (data: PblCitizenCaseCreatePayload) =>
  axios.post<ApiResponse<PblCase>>('/api/pbl/citizen/cases', withPblUploadProofs(data));

export const updateCitizenPblCase = (caseId: number, data: PblCitizenCaseDraftPayload) =>
  axios.patch<ApiResponse<PblCase>>(`/api/pbl/citizen/cases/${caseId}`, withPblUploadProofs(data));

export const submitCitizenPblCase = (caseId: number, data: PblCitizenCaseCreatePayload) =>
  axios.post<ApiResponse<PblCase>>(`/api/pbl/citizen/cases/${caseId}/submit`, withPblUploadProofs(data));

export const createPblCaseAsOcStaff = (data: PblCaseCreatePayload) =>
  axios.post<ApiResponse<PblCase>>('/api/pbl/oc/cases', withPblUploadProofs(data));

export const createPblCase = createPblCaseAsOcStaff;

export const createPblOcIntakeMeeting = (data: PblOcIntakeMeetingCreatePayload) =>
  axios.post<ApiResponse<PblOcIntakeMeetingCreateResponse>>('/api/pbl/oc/intake-meetings', data);

export const getPblOcIntakeSummary = (caseId: number) =>
  axios.get<ApiResponse<PblIntakeSummaryResponse>>(`/api/pbl/oc/cases/${caseId}/intake-summary`);

export const reviewPblCaseByOc = (caseId: number, data: PblCaseReviewPayload) =>
  axios.post<ApiResponse<PblCase>>(`/api/pbl/oc/cases/${caseId}/review`, withPblUploadProofs(data));

export const cancelPblCase = (caseId: number, data: PblCancelCasePayload) =>
  axios.post<ApiResponse<PblCase>>(`/api/pbl/cases/${caseId}/cancel`, data);

export const transitionPblCase = (caseId: number, data: PblCaseTransitionPayload) =>
  axios.post<ApiResponse<PblCase>>(`/api/pbl/cases/${caseId}/transition`, data);

export const reassignPblCaseLawyer = (caseId: number, data: PblReassignLawyerPayload) =>
  axios.post<
    ApiResponse<{
      case: PblCase;
      assignment: PblCaseAssignment;
    }>
  >(`/api/pbl/cases/${caseId}/reassign-lawyer`, data);

export const createPblLawyerExitRequest = (caseId: number, data: PblLawyerExitRequestPayload) =>
  axios.post<ApiResponse<PblLawyerExitRequest>>(`/api/pbl/cases/${caseId}/lawyer-exit-requests`, data);

export const runPblCaseMatching = (caseId: number) =>
  axios.post<ApiResponse<PblMatchingRunResponse | PblMatchingResult[]>>(`/api/pbl/cases/${caseId}/matching/run`, {});

export const getPblCaseMatchingStatus = (caseId: number, signal?: AbortSignal) =>
  axios.get<ApiResponse<PblMatchingStatusResponse>>(`/api/pbl/cases/${caseId}/matching/status`, { signal });

export const recordPblCaseMatchingExposure = (caseId: number, signal?: AbortSignal) =>
  axios.post<ApiResponse<{ recorded: boolean }>>(`/api/pbl/cases/${caseId}/matching/exposure`, {}, { signal });

export const inviteFirstPblCaseMatch = (caseId: number) =>
  axios.post<ApiResponse<PblCaseAssignment>>(`/api/pbl/cases/${caseId}/matching/invite-first`);

export const manualInvitePblLawyer = (caseId: number, data: PblManualInvitePayload) =>
  axios.post<ApiResponse<PblCaseAssignment>>(`/api/pbl/cases/${caseId}/matching/manual-invite`, data);

export const respondPblCaseAssignment = (caseId: number, assignmentId: number, data: PblAssignmentRespondPayload) =>
  axios.post<ApiResponse<PblCase>>(`/api/pbl/cases/${caseId}/assignments/${assignmentId}/respond`, data);

export const createPblDocument = (caseId: number, data: PblDocumentCreatePayload) =>
  axios.post<ApiResponse<PblDocument>>(`/api/pbl/cases/${caseId}/documents`, withPblUploadProofs(data));

export const getPblDocument = (documentId: number) =>
  axios.get<ApiResponse<PblDocument>>(`/api/pbl/documents/${documentId}`);

export const grantPblDocumentAccess = (documentId: number, data: PblDocumentAccessPayload) =>
  axios.post<ApiResponse<PblDocumentAccess>>(`/api/pbl/documents/${documentId}/access`, data);

export const createPblDocumentSignatureRequest = (documentId: number, data: PblDocumentSignatureRequestPayload) =>
  axios.post<ApiResponse<PblDocument>>(`/api/pbl/documents/${documentId}/signature-requests`, data);

export const getPblSignatureRequest = (signatureRequestId: number) =>
  axios.get<ApiResponse<PblSignatureRequest>>(`/api/pbl/signature-requests/${signatureRequestId}`);

export const startPblSignatureRequest = (signatureRequestId: number) =>
  axios.post<ApiResponse<PblSignatureStartResponse>>(`/api/pbl/signature-requests/${signatureRequestId}/start`);

export const createPblDocumentBlockchainRecord = (documentId: number, data: PblBlockchainRecordPayload) =>
  axios.post<ApiResponse<PblBlockchainRecord>>(`/api/pbl/documents/${documentId}/blockchain-records`, data);

export const confirmPblAppointment = (caseId: number) =>
  axios.post<ApiResponse<PblCase>>(`/api/pbl/cases/${caseId}/appointments/confirm`);

export const createPblAppointmentProposal = (caseId: number, data: PblAppointmentProposalCreatePayload) =>
  axios.post<ApiResponse<PblAppointmentProposal>>(`/api/pbl/cases/${caseId}/appointment-proposals`, data);

export const updatePblAppointmentProposal = (proposalId: number, data: PblAppointmentProposalUpdatePayload) =>
  axios.patch<ApiResponse<PblAppointmentProposal>>(`/api/pbl/appointment-proposals/${proposalId}`, data);

export const selectPblAppointmentProposal = (proposalId: number, data: PblAppointmentProposalSelectPayload) =>
  axios.post<ApiResponse<PblAppointmentProposalSelectResponse>>(
    `/api/pbl/appointment-proposals/${proposalId}/select`,
    data
  );

export const respondPblAppointmentOnBehalf = (proposalId: number, data: PblAppointmentProposalSelectPayload & {
  proxy_reason: string;
  citizen_consent_confirmed: true;
}) => axios.post<ApiResponse<PblAppointmentProposalSelectResponse>>(`/api/pbl/appointment-proposals/${proposalId}/oc-response`, data);

export const createPblMeeting = (caseId: number, data: PblMeetingCreatePayload) =>
  axios.post<ApiResponse<PblMeeting>>(`/api/pbl/cases/${caseId}/meetings`, data);

export const updatePblMeeting = (meetingId: number, data: PblMeetingUpdatePayload) =>
  axios.patch<ApiResponse<PblMeeting>>(`/api/pbl/meetings/${meetingId}`, data);

const normalizePblEndpoint = (endpoint: string) => (endpoint.startsWith('/') ? endpoint : `/${endpoint}`);

export const joinPblMeeting = (meeting: number | string, locale?: string) =>
  axios.get<ApiResponse<{ join_url: string; source: 'stored_url' | 'bbb' }>>(
    typeof meeting === 'string' ? normalizePblEndpoint(meeting) : `/api/pbl/meetings/${meeting}/join`,
    { params: locale ? { locale } : undefined }
  );

export const submitPblEvaluation = (caseId: number, data: PblEvaluationPayload) =>
  axios.post<ApiResponse<PblEvaluation>>(`/api/pbl/cases/${caseId}/evaluations`, data);
